/**
 * "Whose side was the problem on?" — the why every teacher or admin act that
 * changes a reading is recorded with: a side, what exactly, and a note for our
 * own records.
 *
 * The side has no default. A pre-selected "Our reading" would be accepted
 * without being read, and the student/system split is the number the reports
 * most need to be honest. The note is never shown to the student and never sent
 * to the AI — that is what the separate model-note box is for.
 *
 * See docs/FeatureDesigns/AssessmentAndMarking/analysis-adjustments-target.md §4.
 */

import {
  ADJUSTMENT_COMMENT_MAX_LENGTH,
  ADJUSTMENT_ORIGIN_LABELS,
  ADJUSTMENT_REASONS_BY_ORIGIN,
  ADJUSTMENT_REASONS_NEEDING_COMMENT,
  ADJUSTMENT_REASON_LABELS,
  type AdjustmentOrigin,
  type AdjustmentReason,
} from '@padcanvas/shared';
import { ChipGroup } from './ChipGroup';
import './AdjustmentWhyFields.css';
import { InfoTip } from './InfoTip';

/**
 * Where a field's words go, said behind an (i) beside its heading — a teacher
 * deciding what to write needs to know whether the AI reads it and who sees it.
 * Once shown as a row of pills, which read as buttons. One component so every
 * dialog says it the same way.
 */
export function FieldDestinations({ ai, student }: {
  /** The re-run's readers and analyzers are given it. */
  ai: boolean;
  /** The student is shown it. */
  student: boolean;
}) {
  const text = ai
    ? `This is given to the AI on the re-run. Teachers and admins see it in History${student ? ', and the student sees it' : '; it is not shown to students'}.`
    : `Following info is only shown to teachers and admins. It is not input to AI${student ? '' : ' and is not shown to students'}.`;
  return <InfoTip text={text} />;
}

export interface AdjustmentWhyValue {
  origin?: AdjustmentOrigin;
  reasonCode?: AdjustmentReason;
  internalComment?: string;
}

/** The sides a person picks from. `policy` is a script's, never a person's. */
const PICKABLE_ORIGINS: AdjustmentOrigin[] = ['student', 'upload', 'system', 'content'];

/** Why the current value cannot be submitted yet, or null when it can. Mirrors
 *  the server's `assertWhy` so the button says so before the request does. */
export function adjustmentWhyProblem(
  value: AdjustmentWhyValue,
  required: { origin?: boolean; reasonCode?: boolean } = {},
): string | null {
  if (required.origin && !value.origin) return 'Say whose side the problem was on';
  if (required.reasonCode && !value.reasonCode) return 'Pick what went wrong';
  if (value.reasonCode && ADJUSTMENT_REASONS_NEEDING_COMMENT.includes(value.reasonCode) && !value.internalComment?.trim()) {
    return '"Other" needs a note for our records';
  }
  return null;
}

export interface AdjustmentWhyFieldsProps {
  value: AdjustmentWhyValue;
  onChange: (value: AdjustmentWhyValue) => void;
  originRequired?: boolean;
  reasonRequired?: boolean;
  disabled?: boolean;
}

export function AdjustmentWhyFields({ value, onChange, originRequired, reasonRequired, disabled }: AdjustmentWhyFieldsProps) {
  const reasons = value.origin ? ADJUSTMENT_REASONS_BY_ORIGIN[value.origin] : [];
  return (
    <div className="awf">
      <div className="awf-head">
        <span className="awf-title">For our records</span>
        <FieldDestinations ai={false} student={false} />
      </div>
      <div className="awf-row">
        <span className="awf-label">
          Whose side was the problem on?{originRequired ? '' : ' (optional)'}
        </span>
        <ChipGroup<AdjustmentOrigin | ''>
          variant="pills"
          ariaLabel="Whose side was the problem on"
          value={value.origin ?? ''}
          options={PICKABLE_ORIGINS.map(o => ({ value: o, label: ADJUSTMENT_ORIGIN_LABELS[o] }))}
          // A new side clears the reason: reasons belong to one side.
          onChange={o => onChange({ ...value, origin: o || undefined, reasonCode: undefined })}
          disabled={disabled}
        />
      </div>

      {reasons.length > 0 && (
        <div className="awf-row">
          <span className="awf-label">What went wrong?{reasonRequired ? '' : ' (optional)'}</span>
          <ChipGroup<AdjustmentReason | ''>
            variant="pills"
            ariaLabel="What went wrong"
            value={value.reasonCode ?? ''}
            options={reasons.map(r => ({ value: r, label: ADJUSTMENT_REASON_LABELS[r] }))}
            onChange={r => onChange({ ...value, reasonCode: r || undefined })}
            disabled={disabled}
          />
        </div>
      )}

      <label className="awf-comment">
        <span className="awf-label">
          Note for our records <span className="awf-aside">(optional)</span>
        </span>
        <input
          type="text"
          value={value.internalComment ?? ''}
          maxLength={ADJUSTMENT_COMMENT_MAX_LENGTH}
          onChange={e => onChange({ ...value, internalComment: e.target.value })}
          placeholder="e.g. student should have written the question number"
          disabled={disabled}
        />
      </label>
    </div>
  );
}

/** The value as sent: blank comment dropped. */
export function adjustmentWhyPayload(value: AdjustmentWhyValue): AdjustmentWhyValue {
  const internalComment = value.internalComment?.trim();
  return {
    ...(value.origin ? { origin: value.origin } : {}),
    ...(value.reasonCode ? { reasonCode: value.reasonCode } : {}),
    ...(internalComment ? { internalComment } : {}),
  };
}
